import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getGoogleClientId, loadGoogleIdentityServices, useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dog, Mail, Lock, User, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export default function AuthPage() {
  const { login, register, forgotPassword, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const googleButtonRef = useRef(null);
  const [mode, setMode] = useState('login');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const isLogin = mode === 'login';
  const isRegister = mode === 'register';
  const isReset = mode === 'reset';
  const googleClientId = getGoogleClientId();

  useEffect(() => {
    if (isReset || !googleClientId || !googleButtonRef.current) return undefined;

    const googleButtonEl = googleButtonRef.current;
    let cancelled = false;
    setGoogleUnavailable(false);

    loadGoogleIdentityServices()
      .then((google) => {
        if (cancelled || !google?.accounts?.id || !googleButtonEl) return;

        google.accounts.id.initialize({
          client_id: googleClientId,
          callback: async (response) => {
            if (!response?.credential) {
              toast.error('Google sign-in did not return a credential.');
              return;
            }

            setGoogleLoading(true);
            try {
              await loginWithGoogle(response.credential);
              toast.success('Signed in with Google!');
              navigate('/dashboard');
            } catch (err) {
              toast.error(err.response?.data?.detail || err.message || 'Google sign-in failed.');
            } finally {
              setGoogleLoading(false);
            }
          },
        });

        googleButtonEl.innerHTML = '';
        const width = Math.min(336, googleButtonEl.parentElement?.clientWidth || 336);
        google.accounts.id.renderButton(googleButtonEl, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          shape: 'pill',
          text: isLogin ? 'signin_with' : 'signup_with',
          logo_alignment: 'left',
          width,
        });
      })
      .catch(() => setGoogleUnavailable(true));

    return () => {
      cancelled = true;
      googleButtonEl.innerHTML = '';
    };
  }, [googleClientId, isLogin, isReset, loginWithGoogle, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isReset) {
        await forgotPassword(form.email);
        toast.success('If an account exists, password reset instructions will be sent.');
        setMode('login');
      } else if (isLogin) {
        await login(form.email, form.password);
        toast.success('Welcome back!');
        navigate('/dashboard');
      } else {
        await register(form.name, form.email, form.password);
        toast.success('Account created!');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (isReset ? 'Password reset is not available yet. Please try again later.' : 'Something went wrong'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12 animate-fade-in" data-testid="auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#4A90E2] rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Dog className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-['Nunito'] font-extrabold text-2xl text-[#1A1D20] tracking-tight">
            {isReset ? 'Reset Password' : isLogin ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-sm text-[#5C636A] mt-1">
            {isReset ? 'Enter your email and we will send reset instructions' : isLogin ? 'Sign in to manage your listings' : 'Join DoggoHub to start posting'}
          </p>
        </div>

        <div className="bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          {!isReset && (
            <div className="mb-5">
              {googleClientId && !googleUnavailable && (
                <div className={googleLoading ? 'pointer-events-none opacity-60' : ''}>
                  <div className="flex justify-center min-h-[44px]" data-testid="google-signin-container">
                    <div ref={googleButtonRef} data-testid="google-signin-button" />
                  </div>
                </div>
              )}
              {googleUnavailable && (
                <p className="text-center text-xs text-[#5C636A]">
                  Google sign-in is temporarily unavailable. Please use email sign-in.
                </p>
              )}
              {googleClientId && (
                <div className="relative my-5">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-3 text-[#5C636A]">or continue with email</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" className="text-xs font-medium text-[#5C636A]">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C636A]" />
                  <Input
                    id="name"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    className="pl-10 h-11 rounded-xl bg-gray-50"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>
            )}
            <div>
              <Label htmlFor="email" className="text-xs font-medium text-[#5C636A]">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C636A]" />
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                  className="pl-10 h-11 rounded-xl bg-gray-50"
                  required
                  data-testid="auth-email-input"
                />
              </div>
            </div>
            {!isReset && (
              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-xs font-medium text-[#5C636A]">Password</Label>
                  {isLogin && (
                    <button
                      type="button"
                      onClick={() => setMode('reset')}
                      className="text-xs font-semibold text-[#4A90E2] hover:underline"
                      data-testid="forgot-password-button"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C636A]" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Min 6 characters"
                    value={form.password}
                    onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                    className="pl-10 h-11 rounded-xl bg-gray-50"
                    required={!isReset}
                    minLength={6}
                    data-testid="auth-password-input"
                  />
                </div>
              </div>
            )}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-[#F5A623] hover:bg-[#e09515] text-white font-bold shadow-[0_4px_14px_rgb(245,166,35,0.4)] cta-button"
              data-testid={isReset ? 'password-reset-submit-button' : 'auth-submit-button'}
            >
              {loading ? 'Please wait...' : isReset ? 'Send Reset Link' : isLogin ? 'Sign In' : 'Create Account'}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </form>

          <p className="text-center text-sm text-[#5C636A] mt-6">
            {isReset ? 'Remembered your password? ' : isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button
              onClick={() => setMode(isReset || isRegister ? 'login' : 'register')}
              className="text-[#4A90E2] font-semibold hover:underline"
              data-testid="toggle-auth-mode"
            >
              {isReset || isRegister ? 'Sign In' : 'Sign Up'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
